import { HeadContent, Scripts, createRootRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Analytics } from '@vercel/analytics/react'
import { CrispChat } from '#bv/components/CrispChat'
import { Header } from '#bv/components/Header'
import { Footer } from '#bv/components/Footer'
import { DiscordIcon } from '#bv/components/DiscordIcon'
import { CurrencyProvider } from '#bv/lib/currency/CurrencyContext'
import { NewShell } from '#bv/newui/NewShell'
import { newUiEnabled } from '#bv/newui/newUiFlag'
import '../styles.css'

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'PulseDigital — Premium Digital Marketplace' },
      { name: 'description', content: 'PulseDigital is the premier destination for Discord products, streaming services, gaming goods, social media growth, and premium software. Fast delivery. Trusted by thousands.' },
      { name: 'theme-color', content: '#050508' },
      { property: 'og:url', content: 'https://blkvault.cc/' },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: 'PulseDigital — Premium Digital Marketplace' },
      {
        property: 'og:description',
        content:
          'Discord products, streaming, gaming, social growth & software. Fast delivery. Trusted by thousands.',
      },
      { property: 'og:image', content: 'https://blkvault.cc/pulsedigital-logo.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://blkvault.cc/' },
      { rel: 'icon', href: '/pulsedigital-logo.png', type: 'image/png' },
      { rel: 'apple-touch-icon', href: '/pulsedigital-logo.png' },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,300&family=JetBrains+Mono:wght@400;500&family=Outfit:wght@400;500;600;700;800;900&display=swap',
      },
    ],
  }),
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const navigate = useNavigate()
  const adminShell = pathname.startsWith('/admin')
  const minimalShell = pathname.startsWith('/internal')
  const useNewUi = newUiEnabled()

  useEffect(() => {
    // Supabase OAuth can return tokens as a URL hash or PKCE ?code= on any page.
    // Route them to the correct handler before the app strips or misroutes them.
    if (typeof window === 'undefined') return
    const hash = window.location.hash || ''
    const search = window.location.search || ''
    const hasSupabaseTokenHash =
      hash.includes('access_token=') || hash.includes('provider_token=')
    const hasPkceCode = search.includes('code=')
    const hasOAuthParams = hasSupabaseTokenHash || hasPkceCode
    if (!hasOAuthParams) return
    if (pathname.startsWith('/auth/callback') || pathname.startsWith('/customer')) return
    if (pathname.startsWith('/admin/login')) return

    let adminPending = false
    try {
      adminPending = sessionStorage.getItem('bv_admin_oauth_pending') === '1'
    } catch {
      // ignore
    }

    if (adminPending) {
      window.location.replace(`${window.location.origin}/admin/login${search}${hash}`)
      return
    }

    const next = encodeURIComponent(pathname === '/' ? '/customer' : pathname)
    window.location.replace(`${window.location.origin}/auth/callback?next=${next}${search}${hash}`)
  }, [navigate, pathname])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (pathname !== '/') return
    const hash = window.location.hash || ''
    const search = window.location.search || ''
    const hasSupabaseTokenHash =
      hash.includes('access_token=') || hash.includes('provider_token=')
    if (hasSupabaseTokenHash) return
    if (search.includes('code=') || search.includes('access_token=')) return

    let adminPending = false
    try {
      adminPending = sessionStorage.getItem('bv_admin_oauth_pending') === '1'
    } catch {
      // ignore
    }
    if (adminPending) return

    if (!search && !hash) return
    window.history.replaceState(null, '', `${window.location.origin}/`)
  }, [pathname])

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="bg-[#050508] text-[#f0f6ff]">
        {!adminShell && !minimalShell ? (
          <CurrencyProvider>
            {useNewUi ? (
              <NewShell>
                <main>{children}</main>
              </NewShell>
            ) : (
              <>
                <Header />
                <main>{children}</main>
                <Footer />
              </>
            )}
          </CurrencyProvider>
        ) : (
          <main>{children}</main>
        )}
        <Analytics />
        {!adminShell && !minimalShell ? <CrispChat /> : null}
        <Scripts />
        {/* Floating Discord Button */}
        {!adminShell && !minimalShell ? (
        <a
          href="https://discord.gg/tGxhDfRkdE"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-discord fixed left-4 bottom-4 z-40 rounded-full px-3 py-2 text-xs shadow-2xl hover:scale-105 sm:left-6 sm:bottom-6 sm:px-4 sm:py-3 sm:text-sm"
          aria-label="Join our Discord"
        >
          <DiscordIcon size={18} white className="shrink-0" />
          <span className="hidden sm:inline">Support</span>
        </a>
        ) : null}
      </body>
    </html>
  )
}
